import FaqSection from '@/components/FaqSection'
import OffersRepeat from '@/components/OffersRepeat'
import ProtocolWhatsappTestimonials from '@/components/ProtocolWhatsappTestimonials'
import MapaWhatsappTestimonials from '@/components/MapaWhatsappTestimonials'
import JornadaCompletaOffer from '@/components/JornadaCompletaOffer'
import { motion } from 'framer-motion'
import { usePageMeta } from '@/hooks/usePageMeta'

// Cores neutras baseadas no tema da experiência (sem elemento específico)
const ACCENT = '#B9A3D9'       // lilás neutro como cor de destaque
const ACCENT_SOFT = '#d4c5e8'

function PageOfertas() {
  usePageMeta({
    title: 'Reative os 4 Elementos — Ofertas',
    description: 'Escolha o caminho completo da sua transformação. Protocolo de Liberação e Cocriação ou Mapa de Cura dos 4 Elementos.',
    ogImage: 'https://static.lumi.new/2a/2ae8843245193092a9f2a8f148dea3ad.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/ofertas/',
  })

  return (
    <div
      className="min-h-screen w-full font-body"
      style={{ background: '#0a0f14', color: 'var(--ink, #e8e0d5)' }}
    >
      {/* Header */}
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p
          className="text-[11px] tracking-[0.35em] uppercase font-body opacity-60"
          style={{ color: ACCENT_SOFT }}
        >
          Reative os <span className="numerals">4</span> Elementos · Mônica Maria
        </p>
      </header>

      {/* Main content */}
      <main className="w-full max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >

          {/* ── HEADER ── */}
          <div className="text-center mb-12 md:mb-16">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
              style={{ color: ACCENT_SOFT }}
            >
              Reative os <span className="numerals">4</span> Elementos
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="space-y-5 max-w-2xl mx-auto"
            >
              <p
                className="font-display type-hero text-[var(--ink,#e8e0d5)]"
                style={{ lineHeight: 1.2 }}
              >
                Você vive ciclos que se repetem:{' '}
                <em className="italic font-light" style={{ color: ACCENT_SOFT }}>
                  escassez, bloqueios nos relacionamentos, procrastinação ou ansiedade?
                </em>
              </p>

              {/* Headline de destaque */}
              <p
                className="font-display text-center"
                style={{
                  fontSize: 'clamp(1.6rem, 4vw, 2.2rem)',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: ACCENT_SOFT,
                  paddingTop: '0.5rem',
                }}
              >
                Libere o que te limita e acesse a vida que você merece.
              </p>

              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 100, opacity: 1 }}
                transition={{ duration: 1.2, delay: 0.4 }}
                className="h-px mx-auto"
                style={{
                  background: `linear-gradient(90deg, transparent, ${ACCENT_SOFT}, transparent)`,
                }}
              />

              <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.8)', lineHeight: 1.75 }}>
                Isso não é azar. É o campo vibracional pedindo atenção.
              </p>
              <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.7)', lineHeight: 1.75 }}>
                Cada um desses padrões corresponde a um elemento bloqueado: Terra, Água, Fogo ou Ar. E quando esse bloqueio é liberado através do reconhecimento e de estímulos de reprogramação noturna o ciclo começa a se quebrar.
              </p>
              <p className="font-body type-body" style={{ color: 'var(--ink, #e8e0d5)', fontWeight: 500, lineHeight: 1.6 }}>
                Escolha por onde quer começar.
              </p>
            </motion.div>
          </div>

          {/* ── OFERTAS ── */}
          <div className="flex flex-col gap-6 md:gap-8">

            {/* ── OFERTA 1 — MAPA DE CURA ── */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7 }}
              className="rounded-sm p-5 sm:p-6 md:p-10 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {/* Side accent bar */}
              <div
                className="absolute left-0 inset-y-0 w-[3px] rounded-l-sm"
                style={{
                  background: `linear-gradient(180deg, ${ACCENT}88, ${ACCENT_SOFT}44, transparent)`,
                }}
              />

              {/* Badge */}
              <div className="flex items-center gap-3 mb-7">
                <span
                  className="text-[10px] tracking-[0.4em] uppercase font-body px-3 py-1 rounded-full"
                  style={{
                    color: ACCENT_SOFT,
                    border: `1px solid ${ACCENT_SOFT}44`,
                    background: `${ACCENT}0e`,
                  }}
                >
                  Oferta <span className="numerals">1</span>
                </span>
                <div
                  className="flex-1 h-px"
                  style={{
                    background: `linear-gradient(90deg, ${ACCENT_SOFT}44, transparent)`,
                  }}
                />
              </div>

              <h2 className="font-display type-section text-[var(--ink,#e8e0d5)] mb-6">
                Mapa de Cura dos{' '}
                <em className="italic font-light" style={{ color: ACCENT_SOFT }}>
                  <span className="numerals">4</span> Elementos
                </em>
              </h2>

              <p className="font-body type-body mb-4" style={{ color: 'rgba(232,224,213,0.65)' }}>
                O Mapa vai à raiz de todos os seus <span className="numerals">4</span> elementos. Você recebe um diagnóstico completo com seus padrões limitantes, sua Trindade de Cura personalizada e seus Decretos Eu Sou escritos especificamente para você.
              </p>
              <p className="font-body type-body mb-8" style={{ color: 'rgba(232,224,213,0.65)' }}>
                Além do seu Mapa personalizado, você recebe acesso completo ao Programa Eleve e Prospere — <span className="numerals">14</span> aulas práticas para trabalhar os <span className="numerals">4</span> elementos no seu dia a dia e elevar sua vibração de forma consistente.
              </p>

              {/* Depoimentos em print do WhatsApp — Mapa */}
              <MapaWhatsappTestimonials accentSoft={ACCENT_SOFT} />

              {/* Price + CTA */}
              <div
                className="pt-7 border-t"
                style={{ borderColor: `${ACCENT_SOFT}22` }}
              >
                <p
                  className="font-display text-[20px] sm:text-[22px] md:text-[28px] leading-none mb-6"
                  style={{ color: ACCENT_SOFT }}
                >
                  <span className="text-[var(--ink,#e8e0d5)] numerals">R$147</span>
                  <span className="font-body ml-2" style={{ fontSize: '1rem', color: 'rgba(232,224,213,0.55)', fontWeight: 400 }}>
                    ou <span className="numerals">12x</span> de <span className="numerals">R$15,20</span>
                  </span>
                </p>

                <a
                  href="https://pay.hotmart.com/V105026857E"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center rounded-xl uppercase transition-all duration-300 hover:brightness-110 hover:-translate-y-[1px] active:translate-y-0"
                  style={{
                    fontSize: 'clamp(1.2rem, 2.6vw, 1.35rem)',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    padding: '20px 28px',
                    minHeight: '60px',
                    lineHeight: 1.4,
                    background: '#8B5E9C',
                    color: '#ffffff',
                    boxShadow: '0 6px 18px rgba(139,94,156,0.28), 0 1px 2px rgba(0,0,0,0.18)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  Quero meu Mapa de Cura
                </a>
              </div>
            </motion.div>

            {/* ── OFERTA 2 — PROTOCOLO / JORNADA COMPLETA ── */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85 }}
              className="rounded-sm p-5 sm:p-6 md:p-10 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {/* Top glow */}
              <div
                className="absolute inset-x-0 -top-32 h-64 pointer-events-none"
                style={{
                  background: `radial-gradient(closest-side, ${ACCENT_SOFT}18, transparent)`,
                }}
              />

              {/* Badge */}
              <div className="flex items-center gap-3 mb-7">
                <span
                  className="text-[10px] tracking-[0.4em] uppercase font-body px-3 py-1 rounded-full"
                  style={{
                    color: ACCENT_SOFT,
                    border: `1px solid ${ACCENT_SOFT}44`,
                    background: `${ACCENT}12`,
                  }}
                >
                  Oferta <span className="numerals">2</span>
                </span>
                <div
                  className="flex-1 h-px"
                  style={{
                    background: `linear-gradient(90deg, ${ACCENT_SOFT}44, transparent)`,
                  }}
                />
              </div>

              <h2 className="font-display type-section text-[var(--ink,#e8e0d5)] mb-6">
                Protocolo de{' '}
                <em className="italic font-light" style={{ color: ACCENT_SOFT }}>
                  Liberação e Cocriação
                </em>
              </h2>

              <div className="space-y-4 mb-8">
                {[
                  'O Protocolo de Liberação e Cocriação vai além do diagnóstico.',
                  'Você recebe uma meditação específica para desbloquear os padrões que estão limitando sua vida e ao mesmo tempo potencializar os elementos que estão em desequilíbrio e que fazem você repetir ciclos negativos. Essa meditação é escutada por 21 noites consecutivas antes de dormir, no momento em que o cérebro transita para o estado theta, a única janela do dia em que o inconsciente fica diretamente acessível sem o filtro do julgamento consciente. É exatamente o estado que hipnoterapeutas induzem artificialmente e que acontece naturalmente toda noite.',
                  'Antes de receber a meditação você preenche um formulário de investigação profunda das crenças, programações familiares herdadas e padrões que se repetem na sua vida. Com base nesse formulário você recebe um Mapa de Revelações, um documento personalizado que nomeia os padrões e as dores que fazem você repetir os mesmos ciclos, e a Trindade de Cura específica para o que foi identificado.',
                  'A meditação é gravada com intenção direcionada para a sua consciência. Antes de gravar me conecto com o seu campo vibracional para que a frequência dessa gravação chegue até você potencializada desde a primeira noite em que escutar.',
                  'A neurociência documenta que a repetição consistente de novos padrões por 21 dias começa a reconfigurar redes neurais. O HeartMath Institute documenta que gravações feitas em estado de coerência cardíaca carregam uma qualidade que afeta o sistema nervoso de quem escuta. Você não está apenas ouvindo uma meditação. Está recebendo um campo de intenção real direcionado para o que está pronto para se dissolver em você.',
                ].map((p, i) => (
                  <p key={i} className="font-body type-body" style={{ color: 'rgba(232,224,213,0.65)' }}>
                    {p}
                  </p>
                ))}
              </div>

              {/* Destaque — gravação humana, sem IA */}
              <div
                className="relative rounded-sm p-5 sm:p-6 md:p-7 mb-8"
                style={{
                  background: `linear-gradient(135deg, ${ACCENT}1a 0%, ${ACCENT}0a 100%)`,
                  border: `1px solid ${ACCENT_SOFT}59`,
                }}
              >
                <div
                  className="absolute left-0 inset-y-0 w-[3px] rounded-l-sm"
                  style={{ background: `linear-gradient(180deg, ${ACCENT_SOFT}, ${ACCENT}, transparent)` }}
                />
                <p
                  className="uppercase font-body mb-3"
                  style={{
                    fontSize: '0.78rem',
                    letterSpacing: '0.3em',
                    color: ACCENT_SOFT,
                    lineHeight: 1.6,
                  }}
                >
                  ✦ Gravado com presença real
                </p>
                <p
                  className="font-body type-body"
                  style={{ color: 'var(--ink, #e8e0d5)' }}
                >
                  Cada meditação é gravada por mim com presença e intenção direcionada especificamente para o seu campo. Nenhuma inteligência artificial é usada na gravação. É a minha voz, a minha energia e a minha conexão com o que o seu campo revelou, criada para você escutar todas as noites por <span className="numerals">21</span> dias.
                </p>
              </div>

              {/* ── DEPOIMENTOS — 5 PRINTS (Graziela, Joene, Zelina, Clarice, Edijania) ── */}
              <div className="mt-8 mb-2">
                <ProtocolWhatsappTestimonials accentSoft={ACCENT_SOFT} />
              </div>

              {/* Jornada Completa de 6 Ciclos */}
              <JornadaCompletaOffer accentSoft={ACCENT_SOFT} hideBonus />
            </motion.div>

          </div>





          {/* ── FAQ ── */}
          <FaqSection accentColor={ACCENT} accentSoft={ACCENT_SOFT} />

          {/* ── OFERTAS REPETIDAS ── */}
          <OffersRepeat
            accentColor={ACCENT}
            accentSoft={ACCENT_SOFT}
          />

          {/* ── JORNADA COMPLETA — após FAQ ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="mt-10 md:mt-14 rounded-sm p-5 sm:p-6 md:p-10 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(200,146,58,0.06) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(200,146,58,0.35)',
            }}
          >
            {/* Top glow dourado */}
            <div
              className="absolute inset-x-0 -top-32 h-64 pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, rgba(200,146,58,0.16), transparent)' }}
            />

            {/* Badge */}
            <div className="flex items-center gap-3 mb-7">
              <span
                className="uppercase font-body px-3 py-1 rounded-full"
                style={{
                  fontSize: '0.78rem',
                  letterSpacing: '0.3em',
                  color: '#E8C77A',
                  border: '1px solid rgba(200,146,58,0.45)',
                  background: 'rgba(200,146,58,0.10)',
                  lineHeight: 1.7,
                }}
              >
                Comece sua jornada de transformação.
              </span>
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(200,146,58,0.45), transparent)' }} />
            </div>

            {/* Título */}
            <h2
              className="font-display text-[var(--ink,#e8e0d5)] mb-3"
              style={{ fontSize: 'clamp(1.6rem, 4vw, 2rem)', fontWeight: 500, lineHeight: 1.18 }}
            >
              Jornada Completa de{' '}
              <em className="italic font-light" style={{ color: '#E8C77A' }}>
                Liberação e Cocriação
              </em>
            </h2>

            {/* Subtítulo */}
            <p
              className="font-body mb-6"
              style={{ fontSize: 'clamp(1.1rem, 2.4vw, 1.25rem)', fontWeight: 500, lineHeight: 1.5, color: '#E8C77A' }}
            >
              <span className="numerals">6</span> Ciclos de <span className="numerals">21</span> dias, <span className="numerals">126</span> dias de transformação progressiva
            </p>

            <ul className="space-y-3 mb-5">
              {[
                'Diagnóstico do elemento mais bloqueado',
                'Mapa de Revelações personalizado',
                'Trindade de Cura personalizada',
                'Decretos Eu Sou de cada ciclo',
                'Meditação gravada com presença real para 21 noites',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="flex-shrink-0" style={{ color: '#E8C77A', fontSize: '1.1rem', lineHeight: 1.6 }}>✦</span>
                  <span className="font-body" style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', lineHeight: 1.7, color: 'rgba(232,224,213,0.8)' }}>
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <p
              className="font-body mb-8"
              style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.78)' }}
            >
              <span className="numerals">6</span> ciclos progressivos de <span className="numerals">21</span> dias. São <span className="numerals">126</span> dias de transformação camada por camada.
            </p>

            {/* Bloco de valor */}
            <div
              className="rounded-sm p-5 sm:p-6 mb-8 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(200,146,58,0.14) 0%, rgba(200,146,58,0.04) 100%)',
                border: '1px solid rgba(200,146,58,0.5)',
              }}
            >
              <p className="font-body mb-2" style={{ fontSize: 'clamp(1.35rem, 3.2vw, 1.7rem)', fontWeight: 600, lineHeight: 1.3, color: '#F0D08A' }}>
                De{' '}
                <span className="numerals" style={{ textDecoration: 'line-through', color: 'rgba(232,224,213,0.45)', fontWeight: 400 }}>R$1.782</span>
                {' '}por{' '}
                <span className="numerals">R$997</span>
                {' '}à vista
              </p>
              <p className="font-body" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.4, color: 'rgba(232,224,213,0.75)' }}>
                ou <span className="numerals">12x</span> de <span className="numerals">R$103,11</span>
              </p>
            </div>

            {/* Botão CTA */}
            <div className="pt-2">
              <a
                href="https://pay.hotmart.com/O105657366F?off=x4c01y5z"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center rounded-xl uppercase transition-all duration-300 hover:brightness-110 hover:-translate-y-[1px] active:translate-y-0"
                style={{
                  fontSize: 'clamp(1.2rem, 2.6vw, 1.35rem)',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  padding: '20px 28px',
                  minHeight: '60px',
                  lineHeight: 1.4,
                  background: '#C8923A',
                  color: '#ffffff',
                  boxShadow: '0 6px 18px rgba(200,146,58,0.28), 0 1px 2px rgba(0,0,0,0.18)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                Quero a Jornada Completa
              </a>
              <p
                className="font-body text-center mt-4"
                style={{ fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.6, color: 'rgba(232,224,213,0.55)', fontStyle: 'italic' }}
              >
                Os ciclos são entregues progressivamente — um a cada <span className="numerals">21</span> dias conforme você avança na jornada.
              </p>
            </div>
          </motion.div>

          {/* ── SESSÃO INDIVIDUAL — bloco final ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-10 md:mt-14 text-center"
          >
            <p
              className="font-body max-w-md mx-auto"
              style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'rgba(232,224,213,0.55)' }}
            >
              Prefere começar com uma sessão individual?{' '}
              <a
                href="https://jornada.reacendaluz.com.br/sessao"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-300"
                style={{ color: 'rgba(201,169,110,0.85)', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#e0c898' }}
                onMouseLeave={e => { e.currentTarget.style.color = 'rgba(201,169,110,0.85)' }}
              >
                Clica aqui
              </a>
            </p>
          </motion.div>

        </motion.div>
      </main>

      {/* Footer */}
      <footer
        className="w-full px-4 py-6 text-center border-t mt-8"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p
          className="text-[10px] tracking-[0.3em] uppercase font-body opacity-40"
          style={{ color: ACCENT_SOFT }}
        >
          Reative os <span className="numerals">4</span> Elementos · Mônica Maria
        </p>
      </footer>
    </div>
  )
}

export default PageOfertas
